import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Palette, 
  Type, 
  Image,
  Save,
  RotateCcw,
  Loader2,
  Upload,
  X,
  Check,
  Sun,
  Moon
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminThemePage = () => {
  const { theme: contextTheme, updateTheme, reloadTheme } = useTheme();
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLight, setUploadingLight] = useState(false);
  const [uploadingDark, setUploadingDark] = useState(false);
  
  const lightLogoInputRef = useRef(null);
  const darkLogoInputRef = useRef(null);

  useEffect(() => {
    loadTheme();
  }, []);

  const loadTheme = async () => {
    try {
      setLoading(true);
      const res = await api.get('/settings/theme');
      setTheme(res.data);
    } catch (error) {
      console.error('Error loading theme:', error);
      // Set defaults
      setTheme({
        colors: {
          primary: '#1a365d',
          primaryLight: '#2a4a7f',
          primaryDark: '#0f2342',
          accent: '#00d4aa',
          accentHover: '#00b38f',
          accentLight: '#e6fbf6',
          alert: '#f59e0b',
          alertLight: '#fef3c7',
        },
        logo: {
          lightUrl: '',
          darkUrl: '',
          useLight: false,
        },
        agency: {
          name: 'MSC L\'AGENCE',
          avatar: '',
        },
        fontFamily: 'Inter'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/theme', theme);
      // Also update the context
      if (updateTheme) {
        await updateTheme(theme);
      }
      if (reloadTheme) {
        await reloadTheme();
      }
      toast.success('Thème sauvegardé avec succès !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateColor = (key, value) => {
    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [key]: value
      }
    }));
  };

  // Handle logo file upload
  const handleLogoUpload = async (file, type) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('L\'image ne doit pas dépasser 5 Mo');
      return;
    }
    
    const setUploading = type === 'light' ? setUploadingLight : setUploadingDark;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/upload?type=logo_${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const imageUrl = response.data.url;
      
      setTheme(prev => ({
        ...prev,
        logo: {
          ...prev.logo,
          [type === 'light' ? 'lightUrl' : 'darkUrl']: imageUrl
        }
      }));
      
      toast.success(`Logo ${type === 'light' ? 'clair' : 'sombre'} uploadé !`);
    } catch (error) {
      console.error('Upload error:', error);
      // Fallback: convert to base64 and store
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setTheme(prev => ({
          ...prev,
          logo: {
            ...prev.logo,
            [type === 'light' ? 'lightUrl' : 'darkUrl']: base64
          }
        }));
        toast.success(`Logo ${type === 'light' ? 'clair' : 'sombre'} ajouté !`);
      };
      reader.readAsDataURL(file);
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = (type) => {
    setTheme(prev => ({
      ...prev,
      logo: {
        ...prev.logo,
        [type === 'light' ? 'lightUrl' : 'darkUrl']: ''
      }
    }));
    toast.success(`Logo ${type === 'light' ? 'clair' : 'sombre'} supprimé`);
  };

  const colorPresets = [
    { name: 'MSC Classique', colors: { primary: '#1a365d', accent: '#00d4aa', alert: '#f59e0b' } },
    { name: 'Moderne Violet', colors: { primary: '#6366f1', accent: '#ec4899', alert: '#f59e0b' } },
    { name: 'Nature Vert', colors: { primary: '#14532d', accent: '#22c55e', alert: '#eab308' } },
    { name: 'Corporate Bleu', colors: { primary: '#1e293b', accent: '#0ea5e9', alert: '#f97316' } },
  ];

  const applyPreset = (preset) => {
    setTheme(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        primary: preset.colors.primary,
        accent: preset.colors.accent,
        alert: preset.colors.alert,
      }
    }));
    toast.success(`Palette "${preset.name}" appliquée`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a365d]">Thème & Couleurs</h1>
          <p className="text-[#64748b]">Personnalisez l'apparence de votre site vitrine</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadTheme}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="logo" className="space-y-6">
        <TabsList>
          <TabsTrigger value="logo"><Image className="w-4 h-4 mr-2" />Logo & Images</TabsTrigger>
          <TabsTrigger value="colors"><Palette className="w-4 h-4 mr-2" />Couleurs</TabsTrigger>
          <TabsTrigger value="typography"><Type className="w-4 h-4 mr-2" />Typographie</TabsTrigger>
        </TabsList>

        {/* LOGO TAB - Now first and with upload */}
        <TabsContent value="logo" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5" />
                Logos de l'agence
              </CardTitle>
              <CardDescription>
                Uploadez vos logos pour le site vitrine et l'espace client. 
                Formats acceptés : PNG, JPG, SVG, WebP (max 5 Mo)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Logo Clair (pour fonds sombres) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-slate-600" />
                    <Label className="text-base font-semibold">Logo clair (fonds sombres)</Label>
                  </div>
                  <p className="text-sm text-slate-500">
                    Ce logo sera affiché sur les fonds sombres (header, footer, etc.)
                  </p>
                  
                  {/* Preview */}
                  <div className="relative p-6 rounded-xl bg-slate-800 flex items-center justify-center min-h-[120px] border-2 border-dashed border-slate-600">
                    {theme?.logo?.lightUrl ? (
                      <>
                        <img 
                          src={theme.logo.lightUrl} 
                          alt="Logo clair" 
                          className="max-h-20 max-w-full object-contain"
                        />
                        <button
                          onClick={() => removeLogo('light')}
                          className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center text-slate-400">
                        <Image className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun logo</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload button */}
                  <input
                    type="file"
                    ref={lightLogoInputRef}
                    onChange={(e) => handleLogoUpload(e.target.files[0], 'light')}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => lightLogoInputRef.current?.click()}
                    disabled={uploadingLight}
                  >
                    {uploadingLight ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploadingLight ? 'Upload en cours...' : 'Uploader le logo clair'}
                  </Button>
                </div>

                {/* Logo Sombre (pour fonds clairs) */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Sun className="w-4 h-4 text-amber-500" />
                    <Label className="text-base font-semibold">Logo sombre (fonds clairs)</Label>
                  </div>
                  <p className="text-sm text-slate-500">
                    Ce logo sera affiché sur les fonds clairs (pages blanches, etc.)
                  </p>
                  
                  {/* Preview */}
                  <div className="relative p-6 rounded-xl bg-white border-2 border-dashed border-slate-200 flex items-center justify-center min-h-[120px]">
                    {theme?.logo?.darkUrl ? (
                      <>
                        <img 
                          src={theme.logo.darkUrl} 
                          alt="Logo sombre" 
                          className="max-h-20 max-w-full object-contain"
                        />
                        <button
                          onClick={() => removeLogo('dark')}
                          className="absolute top-2 right-2 p-1 bg-red-500 hover:bg-red-600 rounded-full text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <div className="text-center text-slate-400">
                        <Image className="w-10 h-10 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">Aucun logo</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload button */}
                  <input
                    type="file"
                    ref={darkLogoInputRef}
                    onChange={(e) => handleLogoUpload(e.target.files[0], 'dark')}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => darkLogoInputRef.current?.click()}
                    disabled={uploadingDark}
                  >
                    {uploadingDark ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    {uploadingDark ? 'Upload en cours...' : 'Uploader le logo sombre'}
                  </Button>
                </div>
              </div>

              {/* Tips */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>💡 Conseil :</strong> Pour un rendu optimal, utilisez des images PNG avec fond transparent 
                  et une résolution minimale de 400x100 pixels.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Agency Name */}
          <Card>
            <CardHeader>
              <CardTitle>Nom de l'agence</CardTitle>
              <CardDescription>Nom affiché lorsque le logo n'est pas disponible</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Nom affiché</Label>
                <Input 
                  value={theme?.agency?.name || ''}
                  onChange={(e) => setTheme({...theme, agency: {...theme.agency, name: e.target.value}})}
                  placeholder="MSC L'AGENCE"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COLORS TAB */}
        <TabsContent value="colors" className="space-y-6">
          {/* Presets */}
          <Card>
            <CardHeader>
              <CardTitle>Palettes prédéfinies</CardTitle>
              <CardDescription>Choisissez un thème de couleurs prédéfini</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => applyPreset(preset)}
                    className="p-4 rounded-lg border-2 border-slate-200 hover:border-amber-400 transition-colors text-left"
                  >
                    <div className="flex gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full" style={{ backgroundColor: preset.colors.primary }} />
                      <div className="w-6 h-6 rounded-full" style={{ backgroundColor: preset.colors.accent }} />
                      <div className="w-6 h-6 rounded-full" style={{ backgroundColor: preset.colors.alert }} />
                    </div>
                    <p className="text-sm font-medium">{preset.name}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Custom Colors */}
          <Card>
            <CardHeader>
              <CardTitle>Couleurs personnalisées</CardTitle>
              <CardDescription>Définissez vos propres couleurs</CardDescription>
            </CardHeader>
            <CardContent className="grid md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label>Couleur principale</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={theme?.colors?.primary || '#1a365d'}
                    onChange={(e) => updateColor('primary', e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input 
                    value={theme?.colors?.primary || '#1a365d'}
                    onChange={(e) => updateColor('primary', e.target.value)}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-slate-500">Header, sidebar, titres</p>
              </div>
              <div className="space-y-2">
                <Label>Couleur d'accent</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={theme?.colors?.accent || '#00d4aa'}
                    onChange={(e) => updateColor('accent', e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input 
                    value={theme?.colors?.accent || '#00d4aa'}
                    onChange={(e) => updateColor('accent', e.target.value)}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-slate-500">Boutons, liens, badges</p>
              </div>
              <div className="space-y-2">
                <Label>Couleur d'alerte</Label>
                <div className="flex gap-2">
                  <Input 
                    type="color" 
                    value={theme?.colors?.alert || '#f59e0b'}
                    onChange={(e) => updateColor('alert', e.target.value)}
                    className="w-12 h-10 p-1 cursor-pointer"
                  />
                  <Input 
                    value={theme?.colors?.alert || '#f59e0b'}
                    onChange={(e) => updateColor('alert', e.target.value)}
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-slate-500">Notifications, alertes</p>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Aperçu en direct</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="p-6 rounded-lg border bg-white">
                <div className="flex items-center gap-4 mb-4 p-3 rounded-lg" style={{ backgroundColor: theme?.colors?.primary }}>
                  <span className="text-white font-bold">{theme?.agency?.name || 'MSC L\'AGENCE'}</span>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: theme?.colors?.primary }}>Exemple de titre</h3>
                <p className="text-slate-600 mb-4">Ceci est un texte d'exemple pour prévisualiser vos couleurs sur le site vitrine.</p>
                <div className="flex gap-2">
                  <button className="px-4 py-2 rounded text-white" style={{ backgroundColor: theme?.colors?.primary }}>
                    Principal
                  </button>
                  <button className="px-4 py-2 rounded text-white" style={{ backgroundColor: theme?.colors?.accent }}>
                    Accent
                  </button>
                  <button className="px-4 py-2 rounded text-white" style={{ backgroundColor: theme?.colors?.alert }}>
                    Alerte
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TYPOGRAPHY TAB */}
        <TabsContent value="typography">
          <Card>
            <CardHeader>
              <CardTitle>Police de caractères</CardTitle>
              <CardDescription>Choisissez la police utilisée sur votre site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Police principale</Label>
                <select 
                  className="w-full p-2 border rounded-lg"
                  value={theme?.fontFamily || 'Inter'}
                  onChange={(e) => setTheme({...theme, fontFamily: e.target.value})}
                >
                  <option value="Inter">Inter</option>
                  <option value="Roboto">Roboto</option>
                  <option value="Open Sans">Open Sans</option>
                  <option value="Poppins">Poppins</option>
                  <option value="Montserrat">Montserrat</option>
                  <option value="Lato">Lato</option>
                </select>
              </div>
              <div className="p-4 rounded-lg border bg-slate-50" style={{ fontFamily: theme?.fontFamily }}>
                <p className="text-lg font-bold mb-2">Aperçu de la police "{theme?.fontFamily || 'Inter'}"</p>
                <p>Voici un exemple de texte avec cette police. ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminThemePage;
